import { useRef, useState } from "react";
import {
  clearBrowserSave,
  defaultFileName,
  downloadJson,
  parseCases,
  serializeCases,
} from "./persistence";
import type { CaseState } from "./caseState";
import type { PipingState } from "./pipingState";

export type SaveStatus =
  | { readonly kind: "idle" }
  | { readonly kind: "saved"; readonly at: Date }
  | { readonly kind: "unavailable" };

/**
 * 저장 상태 표시 + 파일 내보내기·불러오기.
 *
 * 파일은 전부 사용자 PC 안에서 만들어지고 읽힌다. 서버로 올라가지 않는다.
 */
export function SaveBar({
  cases,
  piping,
  status,
  onImport,
  onReset,
}: {
  cases: readonly CaseState[];
  /** 배관 검토도 같은 파일에 담는다 — 한 검토 건이 여러 파일로 갈라지지 않게. */
  piping: PipingState;
  status: SaveStatus;
  onImport: (next: CaseState[], piping: PipingState | null) => void;
  onReset: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<
    { kind: "ok" | "error"; text: string; details?: string[] } | null
  >(null);

  const handleExport = () => {
    downloadJson(serializeCases(cases, piping), defaultFileName());
    setMessage({
      kind: "ok",
      text: `밸브 ${cases.length}건과 배관 검토를 파일로 내려받았습니다.`,
    });
  };

  const handleFile = async (file: File) => {
    const text = await file.text();
    const r = parseCases(text);
    if (!r.ok) {
      setMessage({ kind: "error", text: `불러오기 실패 — ${r.message}` });
      return;
    }
    onImport(r.cases, r.piping);
    setMessage({
      kind: r.warnings.length > 0 ? "error" : "ok",
      text: `밸브 ${r.cases.length}건${r.piping ? "과 배관 검토" : ""}를 불러왔습니다.${
        r.warnings.length > 0 ? ` 확인이 필요한 항목이 ${r.warnings.length}건 있습니다.` : ""
      }`,
      ...(r.warnings.length > 0 ? { details: r.warnings } : {}),
    });
  };

  return (
    <div className="savebar">
      <div className="savebar__status">
        {status.kind === "saved" && (
          <span className="muted">
            이 브라우저에 자동 저장됨 ·{" "}
            {status.at.toLocaleTimeString("ko-KR", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </span>
        )}
        {status.kind === "unavailable" && (
          <span className="muted">
            이 브라우저에는 자동 저장을 할 수 없습니다 — 파일로 내보내
            주십시오.
          </span>
        )}
        {status.kind === "idle" && <span className="muted">&nbsp;</span>}
      </div>

      <div className="savebar__actions">
        <button type="button" className="btn" onClick={handleExport}>
          파일로 내보내기
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => fileInput.current?.click()}
        >
          파일 불러오기
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = ""; // 같은 파일을 다시 고를 수 있게
          }}
        />
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            clearBrowserSave();
            onReset();
            setMessage({ kind: "ok", text: "처음 상태로 되돌렸습니다." });
          }}
        >
          초기화
        </button>
      </div>

      {message && (
        <div
          className={
            "savebar__msg" +
            (message.kind === "error" ? " savebar__msg--error" : "")
          }
          role="status"
        >
          <span>{message.text}</span>
          <button
            type="button"
            className="savebar__close"
            aria-label="알림 닫기"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
          {message.details && (
            <ul className="warnings">
              {message.details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
